import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../hooks/useAlert';
import { useAuth } from '../context/AuthContext';
import { useGuestGate } from '../context/GuestGateContext';
import { kycLinkFromError } from '../context/KycContext';
import { SkeletonForm } from '../components/Skeleton';
import { Dropdown, PageHeader } from '../components/Form';
import { AmountInput, EmptyState, MoneySummary, ReviewSheet, SectionHeader } from '../components/PlayerUI';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Icon from '../components/Icon';

interface Game { id: string; name: string; credentialRequestsEnabled?: boolean }
interface PaymentMethod { id: string; name: string; paymentTag: string | null; logoUrl: string | null; gateway?: string | null }
interface PaymentAccount { id: string; label: string; deepLink: string }

function supportsPlayerAccounts(game: Game) {
  return game.credentialRequestsEnabled !== false;
}

export default function TopUp() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { orderUpdateCount } = useSocket();
  const { alert } = useAlert();
  const { isGuest } = useAuth();
  const { requireAuth } = useGuestGate();
  const [games, setGames] = useState<Game[]>([]);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [game, setGame] = useState<Game | null>(null);
  const [paymentSource, setPaymentSource] = useState('wallet');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [gamePickerOpen, setGamePickerOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [success, setSuccess] = useState<{ amount: number; game: string } | null>(null);

  const refreshWallet = useCallback(async () => {
    if (isGuest) return;
    try {
      const response = await api.get<{ balance: number }>('/wallet/me');
      setWalletBalance(Number(response.data.balance || 0));
    } catch {
      // Balance error is surfaced only if player tries to submit.
    }
  }, [isGuest]);

  useEffect(() => {
    Promise.allSettled([
      api.get<Game[]>('/games'),
      axios.get<PaymentMethod[]>(`${API_BASE}/payment-methods`),
    ]).then(([gameResult, methodResult]) => {
      if (gameResult.status === 'fulfilled') setGames(gameResult.value.data.filter(supportsPlayerAccounts));
      if (methodResult.status === 'fulfilled') setMethods(methodResult.value.data);
      if (gameResult.status === 'rejected' || methodResult.status === 'rejected') alert(t('topup.loadWarningTitle'), t('topup.loadWarningMessage'));
    }).finally(() => setLoading(false));
  }, [alert, t]);

  useEffect(() => { refreshWallet(); }, [refreshWallet, orderUpdateCount]);

  const selectedMethod = methods.find(method => method.id === paymentSource) || null;
  const numericAmount = Number(amount);
  // Solar methods: CashApp-style $5 floor, card $10. Wallet/manual loads: $1.
  const minAmount = selectedMethod?.gateway === 'thesolar'
    ? (/cash\s*app/i.test(selectedMethod.name) ? 5 : 10)
    : 1;
  const validAmount = Number.isInteger(numericAmount) && numericAmount >= minAmount;
  const usesWallet = paymentSource === 'wallet';
  const insufficient = usesWallet && validAmount && numericAmount > walletBalance;
  const canReview = Boolean(game && validAmount && paymentSource && !insufficient);
  const sourceLabel = usesWallet ? t('topup.redesign.slBalance') : selectedMethod?.name || '';
  const destinationLabel = game?.name || t('topup.redesign.game');

  const openReview = () => {
    if (!game) return alert(t('topup.missingInfoTitle'), t('topup.selectGameMessage'));
    if (!validAmount) return alert(t('topup.selectAmountTitle'), t('topup.selectAmountMessage'));
    if (insufficient) return;
    setReviewOpen(true);
  };

  const submitWalletLoad = async () => {
    if (!game || !validAmount) return;
    try {
      await api.post('/orders/topup', {
        gameId: game.id,
        amount: numericAmount,
        target: 'game',
        paymentSource: 'wallet',
      });
      setWalletBalance(balance => Math.max(0, balance - numericAmount));
      setSuccess({ amount: numericAmount, game: game.name });
      setAmount('');
    } catch (error: any) {
      if (error?.response?.data?.code === 'NO_GAME_CREDENTIAL') {
        alert(t('topup.redesign.gameLoginNeededTitle'), t('topup.redesign.gameLoginNeededBody'));
        navigate('/games');
      } else if (error?.response?.data?.code === 'INSUFFICIENT_WALLET_BALANCE') {
        await refreshWallet();
        alert(t('topup.redesign.insufficientTitle'), t('topup.redesign.insufficientBody'));
      } else {
        alert(t('topup.redesign.paymentFailedTitle'), t('topup.redesign.paymentFailedBody'));
      }
    }
  };

  const submitProviderLoad = async () => {
    if (!game || !selectedMethod || !validAmount) return;
    if (selectedMethod.gateway === 'thesolar') {
      const checkoutWindow = window.open('', '_blank');
      try {
        const response = await api.post<{ paymentLink: string }>('/orders/topup/thesolar', {
          paymentMethodId: selectedMethod.id,
          amount: numericAmount,
          target: 'game',
          gameId: game.id,
        });
        if (!response.data?.paymentLink) throw new Error();
        if (checkoutWindow) checkoutWindow.location.href = response.data.paymentLink;
        else window.location.href = response.data.paymentLink;
        navigate('/transactions');
      } catch (error: any) {
        const kyc = kycLinkFromError(error);
        if (kyc && checkoutWindow) {
          checkoutWindow.location.href = kyc;
          navigate('/transactions');
          return;
        }
        checkoutWindow?.close();
        alert(t('topup.redesign.paymentFailedTitle'), t('topup.redesign.paymentFailedBody'));
      }
      return;
    }

    try {
      const accountResponse = await api.get<PaymentAccount>(`/payment-methods/${selectedMethod.id}/random-account`);
      const account = accountResponse.data;
      try { window.open(account.deepLink, '_blank', 'noopener'); } catch { /* Browser may block external applications. */ }
      await api.post('/orders/topup', {
        gameId: game.id,
        paymentMethodId: selectedMethod.id,
        paymentAccountId: account.id,
        amount: numericAmount,
        target: 'game',
        paymentSource: 'method',
      });
      setSuccess({ amount: numericAmount, game: game.name });
      setAmount('');
    } catch {
      alert(t('topup.redesign.paymentFailedTitle'), t('topup.redesign.paymentFailedBody'));
    }
  };

  const confirm = async () => {
    setReviewOpen(false);
    setSubmitting(true);
    try {
      if (usesWallet) await submitWalletLoad();
      else await submitProviderLoad();
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <main className="sls-page">
        <PageHeader title={t('topup.title')} />
        <div className="sls-page-content" style={{ textAlign: 'center', paddingTop: 48 }}>
          <div style={{ width: 64, height: 64, margin: '0 auto 18px', borderRadius: 32, background: colors.accentSoft, display: 'grid', placeItems: 'center' }}><Icon name="check" size={26} color={colors.accent} /></div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>{t('topup.redesign.successTitle')}</h1>
          <p style={{ margin: '10px auto 24px', maxWidth: 360, fontSize: 17, lineHeight: 1.5 }}>{t('topup.redesign.successBody', { amount: success.amount.toFixed(2), game: success.game })}</p>
          <Button label={t('topup.redesign.openGame', { game: success.game })} onClick={() => navigate('/games')} />
          <Button label={t('actions.done')} variant="secondary" onClick={() => setSuccess(null)} style={{ marginTop: 10 }} />
        </div>
      </main>
    );
  }

  return (
    <main className="sls-page">
      <PageHeader title={t('topup.title')} />
      {loading ? <div className="sls-page-content"><SkeletonForm fields={4} /></div> : (
        <div className="sls-page-content">
          <SectionHeader title={t('topup.redesign.stepGame')} />
          <Dropdown icon="gamepad" text={game?.name || ''} placeholder={t('topup.selectGamePlaceholder')} onClick={() => setGamePickerOpen(true)} />

          <SectionHeader title={t('topup.redesign.stepAmount')} />
          <AmountInput value={amount} onChange={setAmount} suggestions={[10, 20, 50, 100]} />

          <SectionHeader title={t('topup.redesign.stepPayWith')} />
          <label style={{ minHeight: 68, display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${colors.line}`, cursor: 'pointer' }}>
            <input type="radio" name="payment-source" checked={usesWallet} onChange={() => setPaymentSource('wallet')} style={{ width: 20, height: 20, accentColor: colors.accent }} />
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 600 }}>{t('topup.redesign.slBalance')}</span>
              <span style={{ display: 'block', marginTop: 3, fontSize: 14, color: colors.textMuted }}>{t('topup.redesign.available', { balance: walletBalance.toFixed(2) })}</span>
            </span>
          </label>
          {methods.map(paymentMethod => (
            <label key={paymentMethod.id} style={{ minHeight: 68, display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${colors.line}`, cursor: 'pointer' }}>
              <input type="radio" name="payment-source" checked={paymentSource === paymentMethod.id} onChange={() => setPaymentSource(paymentMethod.id)} style={{ width: 20, height: 20, accentColor: colors.accent }} />
              {paymentMethod.logoUrl ? <img src={paymentMethod.logoUrl} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} /> : <Icon name="credit-card" size={18} color={colors.text} />}
              <span style={{ fontSize: 17, fontWeight: 600 }}>{paymentMethod.name}</span>
            </label>
          ))}

          {insufficient ? (
            <div style={{ marginTop: 16, padding: 16, borderRadius: 14, background: colors.line }}>
              <div style={{ fontSize: 17, fontWeight: 600 }}>{t('topup.redesign.insufficientTitle')}</div>
              <div style={{ marginTop: 5, fontSize: 15, lineHeight: 1.5 }}>{t('topup.redesign.insufficientBody')}</div>
              <Button label={t('topup.redesign.addFunds')} variant="secondary" onClick={() => navigate('/wallet-topup')} style={{ marginTop: 12 }} />
            </div>
          ) : null}

          <div style={{ marginTop: 28 }}>
            <MoneySummary rows={[
              { label: t('topup.redesign.amount'), value: `$${(validAmount ? numericAmount : 0).toFixed(2)}` },
              { label: t('topup.redesign.from'), value: sourceLabel },
              { label: t('topup.redesign.to'), value: destinationLabel, emphasis: true },
            ]} />
          </div>
          <Button label={game && validAmount ? t('topup.redesign.dynamicAction', { amount: numericAmount.toFixed(2), game: game.name }) : t('topup.redesign.chooseGameAmount')} disabled={!canReview} onClick={() => requireAuth(openReview)} style={{ marginTop: 24 }} />
        </div>
      )}

      <ReviewSheet
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        amount={`$${(validAmount ? numericAmount : 0).toFixed(2)}`}
        source={sourceLabel}
        destination={destinationLabel}
        remaining={usesWallet ? `$${Math.max(0, walletBalance - numericAmount).toFixed(2)}` : undefined}
        timing={usesWallet ? t('topup.redesign.timingWallet') : t('topup.redesign.timingProvider')}
        confirmLabel={game && validAmount ? t('topup.redesign.dynamicAction', { amount: numericAmount.toFixed(2), game: game.name }) : t('topup.redesign.chooseGameAmount')}
        loading={submitting}
        onConfirm={confirm}
      />

      <Modal open={gamePickerOpen} onClose={() => setGamePickerOpen(false)}>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>{t('topup.selectGameTitle')}</div>
        {games.length ? games.map(option => (
          <button key={option.id} onClick={() => { setGame(option); setGamePickerOpen(false); }} style={{ width: '100%', minHeight: 60, display: 'flex', alignItems: 'center', gap: 12, border: 0, borderBottom: `1px solid ${colors.line}`, background: 'transparent', textAlign: 'left', fontSize: 17, fontWeight: 600 }}>
            <Icon name="gamepad" size={17} color={colors.text} />
            <span style={{ flex: 1 }}>{option.name}</span>
            {game?.id === option.id ? <Icon name="check" size={14} color={colors.accent} /> : null}
          </button>
        )) : <EmptyState icon="gamepad" title={t('topup.redesign.noGames')} body={t('topup.redesign.noGamesBody')} action={{ label: t('topup.redesign.viewGames'), onPress: () => navigate('/games') }} />}
        <Button label={t('actions.cancel')} variant="secondary" onClick={() => setGamePickerOpen(false)} style={{ marginTop: 12 }} />
      </Modal>
    </main>
  );
}
